// The AI in the app (docs/ai-plan.md): on or off, what waits in Te controleren, what was put in place, and the
// questions PK asks himself (online only). Without AI everything works as before.

import { useLiveQuery } from "dexie-react-hooks";
import type { Understood } from "@shared/ai";
import { api, ApiError } from "./api";
import { db, getMeta, setMeta, type Task } from "./db";
import { sync } from "./sync";

/** On unless PK switched it off (Meer > Instellingen). */
export function useAiEnabled(): boolean | undefined {
  return useLiveQuery(async () => (await getMeta<boolean>("aiEnabled")) ?? true);
}

export async function aiEnabled(): Promise<boolean> {
  return (await getMeta<boolean>("aiEnabled")) ?? true;
}

export async function setAiEnabled(on: boolean): Promise<void> {
  await setMeta("aiEnabled", on);
  await sync.mutate("settings.setAi", { on }, [], async () => {});
}

/** Te controleren (B31): what the AI was not sure about, oldest first. */
export function useToReview(): Task[] | undefined {
  return useLiveQuery(async () =>
    (await db.tasks.toArray())
      .filter((t) => !t.deleted_at && t.ai_state === "review" && t.ai_proposal)
      .sort((a, b) => (a.created_at < b.created_at ? -1 : 1)),
  );
}

export async function acceptProposal(task: Task, proposal: Understood): Promise<void> {
  // PK put it in place himself: no "Ongedaan maken" bar from AiApplied on top of his own toast.
  await setMeta("aiSeen", [...((await getMeta<string[]>("aiSeen")) ?? []), task.id].slice(-500));
  await sync.mutate("ai.accept", { taskId: task.id, proposal }, [task.id], async () => {
    // The server puts it in place (an appointment, the list); here it leaves Te controleren at once.
    await db.tasks.update(task.id, {
      ai_state: "applied",
      ai_proposal: proposal,
      status:
        proposal.kind === "appointment" || proposal.kind === "date"
          ? "cancelled"
          : proposal.someday && !proposal.date && task.status === "inbox"
            ? "someday"
            : "open",
    });
  });
}

export async function dismissProposal(task: Task): Promise<void> {
  await sync.mutate("ai.dismiss", { taskId: task.id }, [task.id], async () => {
    await db.tasks.update(task.id, { ai_state: "done" });
  });
}

export async function undoApplied(taskId: string): Promise<void> {
  await sync.mutate("ai.undo", { taskId }, [taskId], async () => {
    await db.tasks.update(taskId, { ai_state: "done", status: "inbox" });
  });
}

export interface AiStatus {
  enabled: boolean;
  available: boolean;
  usedMicros: number;
  budgetMicros: number;
}

export const aiStatus = () => api<AiStatus>("/ai/status");
export const aiImage = (mediaType: "image/jpeg" | "image/png", data: string) =>
  api<{ placed: number; review: number }>("/ai/image", { body: { mediaType, data } });
export const aiTidy = () => api<{ proposals: number }>("/ai/tidy", { body: {} });
export const aiSplit = (taskId: string) => api<{ steps: string[] }>("/ai/split", { body: { taskId } });
export interface PlanSuggestion {
  kind: "task" | "routine";
  id: string;
  blockId: string | null;
  reason: string;
  title: string;
}
export const aiPlan = (date: string) => api<{ suggestions: PlanSuggestion[] }>("/ai/plan", { body: { date } });
export const aiLate = (date: string, time: string, candidates: string[]) =>
  api<{ drop: string[]; minutes: number; advice: string }>("/ai/late", { body: { date, time, candidates } });
export const aiLight = (date: string) => api<{ moveIds: string[]; advice: string }>("/ai/light", { body: { date } });
export const aiReview = (weekStart: string) => api<{ summary: string; tip: string }>("/ai/review", { body: { weekStart } });

/** A photo made smaller on the device before it goes to the AI: quicker, and cheaper (at most 1600 px, JPEG). */
export async function shrinkImage(file: File): Promise<{ mediaType: "image/jpeg"; data: string }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const url = canvas.toDataURL("image/jpeg", 0.85);
  return { mediaType: "image/jpeg", data: url.slice(url.indexOf(",") + 1) };
}

/** Why the AI did not answer, in words: the server's own message (budget, not available), or no connection. */
export function aiError(error: unknown, what: string): string {
  if (error instanceof ApiError && error.status >= 400 && error.status < 600 && error.message) return error.message;
  return `${what} kan alleen met verbinding.`;
}
